import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ButtonAction from 'src/components/button-action';
import FormInput from 'src/components/form-input';
import FormSelect from 'src/components/form-select';
import { twMerge } from 'tailwind-merge';

import { INoteNoteCategory } from './type';

interface StandardNodeDialogEditProps {
  id: string;
  open: boolean;
  labelKey: string;
  data: INoteNoteCategory;
  onClose: () => void;
  onSaved: (newData: INoteNoteCategory) => void;
}

export default function NoteNodeDialogEdit({
  id,
  open,
  labelKey,
  data,
  onClose,
  onSaved,
}: StandardNodeDialogEditProps) {
  const [values, setValues] = useState<INoteNoteCategory>({
    text: '',
    color: '#fef08a',
    fontSize: 16,
    bold: false,
    underline: false,
    italic: false,
    lineThrough: false,
  });

  const onSubmitted = () => {
    onSaved({
      ...data,
      ...values,
    });
    toast.success('Save successfully');
  };

  const onCancel = () => {
    setValues((prev) => ({
      ...prev,
      ...data,
    }));
    onClose();
  };

  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      ...data,
    }));
  }, [data]);

  if (!open) return null;
  return (
    <Dialog
      open
      onClose={onCancel}
      className="relative z-50"
      data-testid="note-node-dialog-edit"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={twMerge([
              'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
              'sm:max-w-lg',
            ])}
            data-testid="note-node-dialog-edit-panel"
          >
            <div className="absolute right-2 top-2 flex items-center justify-center gap-2">
              <span className="text-base font-bold text-gray-500">
                {labelKey}
              </span>
              <button
                type="button"
                onClick={onCancel}
                className="h-6 w-6 rounded-full p-1 text-gray-500 hover:bg-gray-100"
                data-testid="note-node-dialog-edit-close-button"
              >
                <XMarkIcon />
              </button>
            </div>

            <div>
              <div className="mt-2 text-left">
                <FormInput
                  id="node-text"
                  name="noteText"
                  label="Content"
                  value={values.text || 'Note'}
                  wrapperClassName=""
                  error=""
                  isTextarea
                  data-testid="note-node-dialog-edit-text-input"
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, text: e.target.value }))
                  }
                />
              </div>
              <div className="mt-2 text-left">
                <FormSelect
                  id="node-font-size"
                  name="noteFontSize"
                  label="Font Size"
                  data-testid="note-node-dialog-edit-font-size-select"
                  options={[
                    { label: '8', value: '8' },
                    { label: '12', value: '12' },
                    { label: '16', value: '16' },
                    { label: '24', value: '24' },
                    { label: '32', value: '32' },
                    { label: '48', value: '48' },
                    { label: '62', value: '62' },
                  ]}
                  value={values.fontSize || '16'}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      fontSize: Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div className="mt-5 w-full sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <ButtonAction
                  type="button"
                  color="blue"
                  variant="contained"
                  onClick={onSubmitted}
                  fullWidth
                  className="sm:col-start-2"
                  data-testid="note-node-dialog-edit-save-button"
                >
                  Save
                </ButtonAction>
                <ButtonAction
                  type="button"
                  color="info"
                  variant="outlined"
                  onClick={onCancel}
                  fullWidth
                  autoFocus
                  className="mt-3 sm:col-start-1 sm:mt-0"
                  data-autofocus
                  data-testid="note-node-dialog-edit-cancel-button"
                >
                  Cancel
                </ButtonAction>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
